import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./styles.module.css";
import { listUsers, createUser, updateUser, deleteUser, resetUserPassword } from "./data";
import { getSessionUser } from "./Auth";

const emptyForm = {
  id: null,
  email: "",
  is_admin: false,
  is_active: true,
  created_at: null,
  updated_at: null,
};

export default function UserPanel({ open = false, onClose }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [me, setMe] = useState(null);

  const panelRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => setIsOpen(!!open), [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [close]);

  useEffect(() => {
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [close]);

  // Open when SettingsPanel dispatches the event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("open-manage-users", handler);
    return () => window.removeEventListener("open-manage-users", handler);
  }, []);

  // Load me + users when opened
  useEffect(() => {
    if (!isOpen) return;
    setMsg(null);
    setTempPassword(null);
    (async () => {
      setLoading(true);
      try {
        const u = await getSessionUser().catch(() => null);
        setMe(u || null);
        const rows = await listUsers();
        setList(rows);
        if (rows?.length) {
          applyRow(rows[0], { clearTemp: true });
        } else {
          setForm(emptyForm);
        }
      } catch (e) {
        setMsg(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyRow = (r, { clearTemp = true } = {}) => {
    setForm({
      id: r.id,
      email: r.email || "",
      is_admin: !!(r.is_admin === 1 || r.is_admin === true || r.is_admin === "1"),
      is_active: !!(r.is_active === 1 || r.is_active === true || r.is_active === "1"),
      created_at: r.created_at || null,
      updated_at: r.updated_at || null,
    });
    if (clearTemp) setTempPassword(null);
  };

  const selectRow = (r) => applyRow(r, { clearTemp: true });

  const newUser = () => { setForm(emptyForm); setTempPassword(null); };

  const copyText = async (text, okMsg = "Copied") => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setMsg(okMsg); setTimeout(() => setMsg(null), 1200); }
    catch { window.prompt(okMsg + " — press Ctrl/Cmd+C, then Enter:", text); }
  };

  const save = async () => {
    setMsg(null);
    const email = String(form.email || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMsg("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      if (form.id) {
        await updateUser(form.id, { email, is_admin: !!form.is_admin, is_active: !!form.is_active });
        const rows = await listUsers();
        setList(rows);
        const updated = rows.find((r) => r.id === form.id);
        if (updated) applyRow(updated, { clearTemp: true });
        setMsg("Saved.");
        setTimeout(() => setMsg(null), 1200);
      } else {
        const resp = await createUser({ email, is_admin: !!form.is_admin });
        const rows = await listUsers();
        setList(rows);
        const created = rows.find((r) => r.id === resp.id) || rows[rows.length - 1];
        if (created) applyRow(created, { clearTemp: false });
        if (resp.temp_password) {
          setTempPassword(resp.temp_password);
          try {
            await navigator.clipboard.writeText(resp.temp_password);
            setMsg("Temporary password copied.");
            setTimeout(() => setMsg(null), 1600);
          } catch {}
        }
      }
    } catch (e) {
      setMsg(e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    if (me && Number(me.id) === Number(form.id)) {
      setMsg("You cannot delete your own account.");
      return;
    }
    if (!window.confirm("Disable this user account?")) return;
    setLoading(true);
    setMsg(null);
    try {
      await deleteUser(form.id);
      const rows = await listUsers();
      setList(rows);
      newUser();
      setMsg("Disabled.");
      setTimeout(() => setMsg(null), 1200);
    } catch (e) {
      setMsg(e.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const resetPwd = async () => {
    if (!form.id) { setMsg("Save the user first."); return; }
    if (!window.confirm("Reset password for this user? They will be forced to change it on next login.")) return;
    setLoading(true);
    setMsg(null);
    try {
      const out = await resetUserPassword(form.id); // -> { temp_password }
      if (out?.temp_password) {
        setTempPassword(out.temp_password);
        try { await navigator.clipboard.writeText(out.temp_password); setMsg("Temporary password copied."); setTimeout(() => setMsg(null), 1600); } catch {}
      }
      const rows = await listUsers();
      setList(rows);
      const updated = rows.find((r) => r.id === form.id);
      if (updated) applyRow(updated, { clearTemp: false });
    } catch (e) {
      setMsg(e.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const isSelf = me && Number(me.id) === Number(form.id);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div role="presentation" onClick={close} style={{ position: "fixed", inset: 0, background: "color-mix(in oklab, black 35%, transparent)", zIndex: 1067 }} />
      {/* Dialog */}
      <div
        ref={panelRef}
        className={styles.filterPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Users"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: Math.min(920, window.innerWidth - 24 * 2),
          maxWidth: "96vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          zIndex: 1068,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.18)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={styles.filterPanelHeader}
          style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1, borderBottom: "1px solid #eee" }}
        >
          <div className={styles.filterTitle}>Users</div>
          <button type="button" className={styles.xBtn} onClick={close} aria-label="Close">×</button>
        </div>

        <div className={styles.filterSection} style={{ overflow: "auto", flex: "1 1 auto", padding: 16 }}>
          <div className={styles.muted} style={{ minHeight: 18 }}>{loading ? "Working…" : msg || ""}</div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(420px, 300px) 1fr", gap: 16, alignItems: "start" }}>
            {/* LEFT: list */}
            <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 8, background: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div className={styles.fieldLabel}>All users</div>
                <button type="button" className={styles.applyBtn} onClick={newUser}>New</button>
              </div>

              <div role="list" aria-label="Users">
                {list.length === 0 && (
                  <div className={styles.muted} style={{ padding: 8, textAlign: "center" }}>No users yet.</div>
                )}

                {list.map((r) => {
                  const selected = form.id === r.id;
                  const active = !!(r.is_active === 1 || r.is_active === true || r.is_active === "1");
                  const admin = !!(r.is_admin === 1 || r.is_admin === true || r.is_admin === "1");
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectRow(r)}
                      role="listitem"
                      aria-selected={selected ? "true" : "false"}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "1px solid " + (selected ? "#c7d2fe" : "#eee"),
                        background: selected ? "#eef2ff" : "#fafafa",
                        borderRadius: 8,
                        padding: 10,
                        marginBottom: 8,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.email || ""}>
                          {r.email}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <span style={{
                            fontSize: 12, padding: "2px 8px", borderRadius: 999,
                            background: active ? "#e8f5e9" : "#f5f5f5",
                            border: "1px solid " + (active ? "#c8e6c9" : "#eee"),
                            color: active ? "#2e7d32" : "#666",
                            alignSelf: "flex-start",
                          }}>{active ? "Active" : "Inactive"}</span>
                          {admin && (
                            <span style={{
                              fontSize: 12, padding: "2px 8px", borderRadius: 999,
                              background: "#e3f2fd", border: "1px solid #bbdefb", color: "#1565c0",
                              alignSelf: "flex-start",
                            }}>Admin</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: editor */}
            <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12, background: "#fff" }}>
              <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>
                {form.id ? "Edit user" : "Create user"}
              </div>

              <div className={styles.inputWrap}>
                <label className={styles.fieldLabel}>Email</label>
                <input
                  className={styles.textInput}
                  value={form.email}
                  onChange={(e) => onField("email", e.target.value)}
                  placeholder="name@example.com"
                  inputMode="email"
                  autoComplete="off"
                />
              </div>

              <div className={styles.inputWrap} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input id="user-admin" type="checkbox" checked={!!form.is_admin} onChange={(e) => onField("is_admin", !!e.target.checked)} />
                <label htmlFor="user-admin" className={styles.fieldLabel} style={{ margin: 0 }}>Admin</label>
              </div>

              {form.id && (
                <div className={styles.inputWrap} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    id="user-active"
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => onField("is_active", !!e.target.checked)}
                    disabled={isSelf}
                  />
                  <label htmlFor="user-active" className={styles.fieldLabel} style={{ margin: 0 }}>
                    Active {isSelf && <span className={styles.muted}>(you can’t deactivate yourself)</span>}
                  </label>
                </div>
              )}

              {/* Temp password block (create or after reset) */}
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  border: "1px solid #eee",
                  borderRadius: 8,
                  background: "#fafafa",
                }}
              >
                <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>
                  Temporary password
                </div>
                <div className={styles.inputWrap}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                    <input
                      className={styles.textInput}
                      value={tempPassword || ""}
                      readOnly
                      placeholder={tempPassword ? "" : (form.id ? "Click “Reset password” to generate" : "Will be generated on create")}
                    />
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => copyText(tempPassword, "Password copied")}
                      disabled={!tempPassword}
                      title={tempPassword ? "Copy password" : "No password available"}
                    >
                      Copy
                    </button>
                  </div>
                  <div className={styles.muted} style={{ marginTop: 6 }}>
                    Shown only once. User must change it at next login.
                  </div>
                </div>
              </div>

              <div className={styles.filterActions} style={{ marginTop: 12 }}>
                {form.id ? (
                  <>
                    <button type="button" className={styles.resetBtn} onClick={remove} disabled={isSelf} title={isSelf ? "You cannot delete your own account" : "Disable user"}>
                      Delete
                    </button>
                    <div className={styles.flexSpacer} />
                    <button type="button" className={styles.secondaryBtn} onClick={resetPwd}>
                      Reset password
                    </button>
                    <button type="button" className={styles.applyBtn} onClick={save}>
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.flexSpacer} />
                    <button type="button" className={styles.applyBtn} onClick={save}>
                      Create
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
